import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ClipboardList, Crosshair, Search, Target } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import MissingTargetNotice from "../../components/MissingTargetNotice";
import { Badge, Button, Loading, NoDataYet, Select } from "../../components/ui";
import { useApi } from "../../api/client";
import { Competition, MatchInfo, StringRecord } from "../../data/types";
import { formatScoreCentres, shotLabel } from "../../utils/score";
import { formatLength, formatTemp, formatVelocity, lengthLabel, useUnits } from "../../utils/units";

/*
 * The complete shot log — every string that has been imported, expandable to
 * every individual shot exactly as the workbook recorded it (ring value, mm
 * coordinates, derived MOA, velocity, temperature, time, sighter flag).
 * The results pages summarise; this page is the raw record behind them.
 */

/** One shot row, raw values first, derived MOA alongside. */
function ShotRows({ s }: { s: StringRecord }) {
  // Deleted shots were struck out, so they are neither scoring nor sighters.
  const scoring = s.shots.filter((x) => !x.sighter && !x.deleted).length;
  const deleted = s.shots.filter((x) => x.deleted).length;
  const { units } = useUnits();
  /* The mm columns stay raw — this table is the audit trail, showing exactly
     what the workbook carried. Only the derived columns follow the reader. */
  const mmPerMoa = s.targetFace?.mmPerMoa ?? null;
  const lenLabel = lengthLabel(units.length, mmPerMoa);
  return (
    <tr>
      <td colSpan={10} className="bg-slate-50/70 px-4 py-3">
        <div className="overflow-x-auto rounded-lg border border-line bg-white">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">#</th>
                <th className="th">Type</th>
                <th className="th">Source</th>
                <th className="th text-right">Value</th>
                <th className="th text-right">x (mm)</th>
                <th className="th text-right">y (mm)</th>
                <th className="th text-right">X ({lenLabel})</th>
                <th className="th text-right">Y ({lenLabel})</th>
                <th className="th text-right">v ({units.velocity})</th>
                <th className="th text-right">Temp (°{units.temp})</th>
                <th className="th text-right">Time (local)</th>
                <th className="th text-right">UTC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {s.shots.map((x) => (
                <tr
                  key={x.n}
                  className={x.deleted ? "bg-danger-soft/40 line-through decoration-danger/60" : x.sighter ? "bg-amber-50/60" : ""}
                >
                  <td className="td font-bold">{shotLabel(x)}</td>
                  <td className="td">
                    {/*
                      What the range decided about this shot. A deleted shot was
                      struck out and counts for nothing; an inserted miss was put
                      back in and counts as a zero. Both are shown rather than
                      hidden — this table is the audit trail behind the score.
                    */}
                    {x.deleted ? (
                      <span className="rounded-md bg-danger-soft px-2 py-0.5 text-[11px] font-bold uppercase text-danger">Deleted</span>
                    ) : x.insertedMiss ? (
                      <span className="rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-bold uppercase text-violet-600">
                        Inserted Miss
                      </span>
                    ) : x.sighter ? (
                      <span className="rounded-md bg-warning-soft px-2 py-0.5 text-[11px] font-bold uppercase text-warning">
                        {x.excluded ? "Excluded" : "Sighter"}
                      </span>
                    ) : (
                      <span className="rounded-md bg-success-soft px-2 py-0.5 text-[11px] font-bold uppercase text-success">Scoring</span>
                    )}
                  </td>
                  <td className="td text-muted">{x.source ?? "—"}</td>
                  <td className={`td text-right font-extrabold ${x.deleted ? "text-muted" : x.isV ? "text-danger" : ""}`}>{x.score}</td>
                  <td className="td text-right">{x.x_mm ?? "—"}</td>
                  <td className="td text-right">{x.y_mm ?? "—"}</td>
                  <td className="td text-right text-muted">{formatLength(x.x, units.length, mmPerMoa)}</td>
                  <td className="td text-right text-muted">{formatLength(x.y, units.length, mmPerMoa)}</td>
                  <td className="td text-right">{formatVelocity(x.velocity, units.velocity)}</td>
                  <td className="td text-right">{formatTemp(x.temp_c, units.temp)}</td>
                  <td className="td text-right text-muted">{x.time}</td>
                  <td className="td text-right text-muted">{x.dateUTC ? x.dateUTC.slice(11, 19) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* The block header exactly as the workbook carries it: keys, target
            face, frame and the four calibration figures. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-muted">
          <span>
            <b className="text-ink">{formatScoreCentres(s.totalScore, s.vCount, s.centreLabel)}</b>
            {s.possible ? ` of ${s.possible}` : ""} · {scoring} scoring + {s.shots.length - scoring - deleted} sighter
            {s.shots.length - scoring - deleted === 1 ? "" : "s"}
            {deleted ? ` · ${deleted} deleted` : ""}
          </span>
          {s.avgVelocity ? <span>avg velocity {formatVelocity(s.avgVelocity, units.velocity, true)}</span> : null}
          {s.conditions && <span>{s.conditions}</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-muted">
          <span>EID <b className="text-ink">{s.eid ?? "—"}</b></span>
          <span>MID <b className="text-ink">{s.mid ?? "—"}</b></span>
          <span>face <b className="text-ink">{s.target || "—"}</b></span>
          {s.calibration && (
            <span>
              mic <b className="text-ink">{s.calibration.micW ?? "—"}×{s.calibration.micH ?? "—"} mm</b> · cal{" "}
              <b className="text-ink">x {s.calibration.xCal ?? "—"}, y {s.calibration.yCal ?? "—"} mm</b>
            </span>
          )}
          <span>
            workbook label “{s.sourceLabel}” · SID column {s.sourceSid ?? "—"}
          </span>
        </div>
      </td>
    </tr>
  );
}

export default function ShotLogs() {
  const [params] = useSearchParams();
  const { data: strings, loading } = useApi<StringRecord[]>("/strings", []);
  const { data: competitions } = useApi<Competition[]>("/competitions", []);
  const { data: matches } = useApi<MatchInfo[]>("/matches", []);
  // Faces these logs refer to that have no stored definition.
  const { data: missingFaces } = useApi<string[]>("/target-faces/missing", []);
  // Distinguishes "none imported yet" from "a few names missing" in the notice.
  const { data: storedFaces } = useApi<{ name: string }[]>("/target-faces", []);

  // ?sid=12222 pre-filters, so other pages can link straight to one shooter's log.
  const [search, setSearch] = useState(params.get("sid") || "");
  // Filters hold ids, not names — see competitionOptions below.
  const [compFilter, setCompFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [clubFilter, setClubFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const compName = (id: string) => competitions.find((c) => c.id === id)?.name ?? id;
  const matchName = (id: string) => matches.find((m) => m.id === id)?.name ?? id;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return strings.filter(
      (s) =>
        (!q || `${s.shooter} ${s.sid} ${s.eid ?? ""} ${s.mid ?? ""} ${s.sourceLabel ?? ""}`.toLowerCase().includes(q)) &&
        (!compFilter || s.competitionId === compFilter) &&
        (!matchFilter || s.matchId === matchFilter) &&
        (!classFilter || (s.rifleClassTag ?? "") === classFilter) &&
        (!clubFilter || (s.club ?? "") === clubFilter) &&
        (!dateFilter || (s.date ?? "") === dateFilter)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strings, search, compFilter, matchFilter, classFilter, clubFilter, dateFilter, competitions, matches]);

  /*
   * A shoot of 250 competitors puts thousands of strings on this page, so it is
   * filtered by the things a scorer actually looks one up by — beyond the
   * competition and match, that is the class, the club and the day.
   */
  const distinct = (pick: (s: StringRecord) => string | undefined) =>
    Array.from(new Set(strings.map((s) => pick(s) || "").filter(Boolean))).sort();

  const totalShots = filtered.reduce((a, s) => a + s.shots.length, 0);

  /*
   * Both filters carry ids rather than names, because names repeat: two shoots
   * can be called the same thing, and "Match 1 — 800 Yards" exists in almost
   * every meeting. Filtering by name silently merged them, so picking one match
   * showed the logs of every match that happened to share its name.
   *
   * The match list is narrowed by the chosen competition and labelled with it
   * when it is not, since the name alone does not say which meeting it belongs to.
   */
  const competitionOptions = useMemo(() => {
    const ids = Array.from(new Set(strings.map((s) => s.competitionId)));
    const counts = new Map<string, number>();
    ids.forEach((id) => counts.set(compName(id), (counts.get(compName(id)) || 0) + 1));
    return [
      { value: "", label: "All Events" },
      ...ids.map((id) => ({
        value: id,
        label: (counts.get(compName(id)) || 0) > 1 ? `${compName(id)} (${id})` : compName(id),
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strings, competitions]);

  const matchOptions = useMemo(() => {
    // The competition's own disambiguated label, so two meetings of the same
    // name do not produce two match entries that read identically.
    const compLabel = new Map(competitionOptions.map((o) => [o.value, o.label]));
    const ids = Array.from(
      new Set(strings.filter((s) => !compFilter || s.competitionId === compFilter).map((s) => s.matchId))
    );
    return [
      { value: "", label: "All Matches" },
      ...ids.map((id) => {
        const m = matches.find((x) => x.id === id);
        return {
          value: id,
          label:
            compFilter || !m ? matchName(id) : `${matchName(id)} — ${compLabel.get(m.competitionId) ?? compName(m.competitionId)}`,
        };
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strings, matches, competitions, competitionOptions, compFilter]);
  const toggle = (id: string) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const allOpen = filtered.length > 0 && filtered.every((s) => open[s.id]);

  return (
    <div>
      <AdminTopbar
        title="Shooter Strings"
        subtitle={`The raw shot log behind the results — ${filtered.length} string${filtered.length === 1 ? "" : "s"}, ${totalShots} shots. Expand a string to see every shot as the workbook recorded it.`}
      />

      {/*
        Raised here as well as on the individual target, because this is the page
        an administrator checks a shoot from — the point at which a missing
        definition is worth acting on.
      */}
      <MissingTargetNotice faces={missingFaces} storedCount={storedFaces.length} className="mb-3" />

      {/* Filters */}
      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[220px] flex-1">
          <label className="label">Search</label>
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Shooter, SID, EID, MID or workbook label..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="label">Event</label>
          <Select
            options={competitionOptions}
            value={compFilter}
            onChange={(v) => {
              setCompFilter(v);
              setMatchFilter(""); // the chosen match may not belong to the new competition
            }}
            className="w-full sm:w-56"
          />
        </div>
        <div>
          <label className="label">Match</label>
          <Select options={matchOptions} value={matchFilter} onChange={setMatchFilter} className="w-full sm:w-56" />
        </div>
        <button
          onClick={() => setOpen(allOpen ? {} : Object.fromEntries(filtered.map((s) => [s.id, true])))}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
        >
          <ClipboardList size={14} /> {allOpen ? "Collapse All" : "Expand All"}
        </button>
        <div>
          <label className="label">Class</label>
          <Select
            options={[{ value: "", label: "All Classes" }, ...distinct((s) => s.rifleClassTag).map((v) => ({ value: v, label: v }))]}
            value={classFilter}
            onChange={setClassFilter}
            className="w-full sm:w-36"
          />
        </div>
        <div>
          <label className="label">Club</label>
          <Select
            options={[{ value: "", label: "All Clubs" }, ...distinct((s) => s.club).map((v) => ({ value: v, label: v }))]}
            value={clubFilter}
            onChange={setClubFilter}
            className="w-full sm:w-40"
          />
        </div>
        <div>
          <label className="label">Date</label>
          <Select
            options={[{ value: "", label: "All Dates" }, ...distinct((s) => s.date).map((v) => ({ value: v, label: v }))]}
            value={dateFilter}
            onChange={setDateFilter}
            className="w-full sm:w-40"
          />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setCompFilter("");
            setMatchFilter("");
            setClassFilter("");
            setClubFilter("");
            setDateFilter("");
          }}
        >
          Reset
        </Button>
      </div>
      <div className="card mt-3 table-scroll [--table-offset:452px]">
        <table className="w-full min-w-[980px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th w-8" />
              <th className="th">Date</th>
              <th className="th">Shooter</th>
              <th className="th">Event</th>
              <th className="th">Match</th>
              <th className="th">Class</th>
              <th className="th">Frame</th>
              <th className="th text-right">Score</th>
              <th className="th text-right">Shots</th>
              <th className="th text-center">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading && (
              <tr>
                <td colSpan={10}><Loading label="Loading shot logs..." /></td>
              </tr>
            )}
            {!loading && !strings.length && (
              <tr>
                <td colSpan={10}><NoDataYet what="shot logs" /></td>
              </tr>
            )}
            {!loading && !!strings.length && !filtered.length && (
              <tr>
                <td colSpan={10} className="td py-10 text-center text-muted">No strings match the current filters.</td>
              </tr>
            )}
            {filtered.map((s) => (
              <React.Fragment key={s.id}>
                <tr onClick={() => toggle(s.id)} className="cursor-pointer transition-colors hover:bg-slate-50">
                  <td className="td text-muted">{open[s.id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                  <td className="td whitespace-nowrap text-muted">{s.date}</td>
                  <td className="td">
                    <Link
                      to={`/admin/shooters/${s.sid}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-bold text-ink hover:text-primary"
                    >
                      {s.shooter}
                    </Link>
                    <div className="text-xs text-muted">SID: {s.sid}</div>
                  </td>
                  <td className="td text-slate-600">{compName(s.competitionId)}</td>
                  <td className="td">{matchName(s.matchId)}</td>
                  <td className="td">
                    <Badge tone="upcoming">{s.rifleClassTag ?? "—"}</Badge>
                  </td>
                  <td className="td text-muted">{s.frame || "—"}</td>
                  <td className="td text-right font-bold">
                    {formatScoreCentres(s.totalScore, s.vCount, s.centreLabel)}
                    {s.possible ? <span className="ml-1 text-xs font-semibold text-muted">/ {s.possible}</span> : null}
                  </td>
                  <td className="td text-right">{s.shots.length}</td>
                  <td className="td text-center">
                    <Link
                      to={`/admin/strings/${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line text-primary transition-colors hover:bg-primary-soft"
                      title="Open the target map"
                    >
                      <Crosshair size={14} />
                    </Link>
                  </td>
                </tr>
                {open[s.id] && <ShotRows s={s} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 flex items-center gap-2 text-[13px] text-muted">
        <Target size={14} className="shrink-0" />
        Sighter rows are highlighted; “Deleted” shots were struck out on the range and score nothing; “Inserted Miss” was added back in and scores zero; “Excluded” marks extra scoring shots that did not count (only the last {""}
        declared number of shots score). Coordinates are millimetres from centre as recorded, with the derived MOA beside them.
      </p>
    </div>
  );
}
